import type { FrefListId, FrefListName } from './encodeFref';
import { getQueryStringValue } from './getQueryStringValue';

const FREF_LIST_ID_BY_CODE_MAP = {
    splp: 'standard plp',
    rplp: 'related plp',
} satisfies Record<FrefListId, string>;

const FREF_LIST_NAME_BY_CODE_MAP = {
    bia: 'buy again',
    biacart: 'buy again cart',
    fbt: 'frequently bought together',
} satisfies Record<FrefListName, string>;

// Codes parsed out of a URL are arbitrary strings, so an unknown one has to surface as `undefined`.
const lookupListLabel = (map: Record<string, string>, code: string): string | undefined => map[code];

export type DecodedFref = {
    item_list_id: string;
    item_list_name: string;
    index: number;
};

export type FrefListCodes = {
    listId: FrefListId;
    listName: FrefListName;
};

export type FrefListAttribution = Omit<DecodedFref, 'index'>;

/**
 * Resolves the GA4 item list labels for a pair of `fref` codes, without needing a URL or a position.
 *
 * @remarks
 * Lets a carousel derive its `select_item` list fields from the same codes it encodes into the outgoing
 * PDP link, so link attribution and event attribution cannot drift apart.
 *
 * @example
 * resolveFrefListAttribution({ listId: 'rplp', listName: 'fbt' });
 * // => { item_list_id: 'related plp', item_list_name: 'frequently bought together' }
 */
export const resolveFrefListAttribution = ({ listId, listName }: FrefListCodes): FrefListAttribution => ({
    item_list_id: FREF_LIST_ID_BY_CODE_MAP[listId],
    item_list_name: FREF_LIST_NAME_BY_CODE_MAP[listName],
});

/**
 * Parses a string in the same shape as the `fref` query param (`{listIdCode}_{listNameCode}_{position}`).
 * Used for PDP/cart attribution stored in `pdpFindingMethodBySku` (e.g. `splp_bia_3`) and for `?fref=`.
 *
 * @returns A {@link DecodedFref} when parsing succeeds, or `null` if the value is missing, does not split into
 * exactly three parts, uses an unknown list id or name code, or the position is not a finite integer greater than zero.
 */
export const parseFrefStyleFindingMethod = (raw: string | null | undefined): DecodedFref | null => {
    if (!raw || raw === '') return null;

    const parts = raw.split('_');
    if (parts.length !== 3) return null;

    const [listIdCode, listNameCode, indexStr] = parts;

    const item_list_id = lookupListLabel(FREF_LIST_ID_BY_CODE_MAP, listIdCode);
    const item_list_name = lookupListLabel(FREF_LIST_NAME_BY_CODE_MAP, listNameCode);
    const index = Number(indexStr);

    if (!item_list_id || !item_list_name || !Number.isFinite(index) || index <= 0) {
        return null;
    }

    return { item_list_id, item_list_name, index };
};

/**
 * Reads the `fref` query parameter from the current URL and maps shorthand segment codes to GA4 item list fields.
 *
 * @remarks
 * Expected shape: `{listIdCode}_{listNameCode}_{position}` (three underscore-separated parts).
 * Examples: `splp_bia_3`, `splp_biacart_3`, `rplp_fbt_2`.
 *
 * @returns A {@link DecodedFref} when parsing succeeds, or `null` if `fref` is missing, does not split into
 * exactly three parts, uses an unknown list id or name code, or the position is not a finite integer greater than zero.
 *
 * @example
 * // Given `?fref=splp_bia_3`
 * decodeFref();
 * // => { item_list_id: 'standard plp', item_list_name: 'buy again', index: 3 }
 */
export const decodeFref = (): DecodedFref | null => {
    const fref = getQueryStringValue('fref');
    return parseFrefStyleFindingMethod(fref);
};
